import { forwardRef, JSX, useImperativeHandle, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import Autocomplete, { type IAutocomplete, type IAutocompleteHistoryProps } from '../AutocompleteBase';
import type { IAutocompleteItem } from '../../../models/autocomplete-item.model';
import { VersionedLineApiModel } from './models/versioned-line.api-model';
import { LineHelper } from '../../../helpers/line.helper';
import type { ILine } from '../../../models/line.model';
import { TranslationService } from '../../../services/translation.service';
import ValidationInfo from '../../../validation/validation-info';
import RequiredAsterisk from '../RequiredAsterisk';
import type IValidatable from '../validatable.interface';
import ValidationEngine from '../../../validation/validation-engine';
import type { IServerValidationError } from '../validatable.interface';
import { AutocompleteLabelPosition, AutocompleteSuggestionSource } from '../AutocompleteBase/autocomplete-base.enum';
import { VersionedLineHelper } from '../../../helpers/versioned-line.helper';
import LineDescription from '../LineDescription';

// === INTERFACES ===
export interface ILineAutocompleteState {
  value: ILine | undefined;
  validationInfo: ValidationInfo;
}

interface ILineAutocompleteProps {
  id: string;
  serverValidationName?: string;
  labelText?: string;
  labelClassName?: string;
  labelPosition?: AutocompleteLabelPosition;
  placeholderText?: string;
  required?: boolean;
  className?: string;
  noResultsText?: string;
  disabled?: boolean;
  historyProps?: IAutocompleteHistoryProps;
  filterResults?: (line: ILine) => boolean;
  state: ILineAutocompleteState;
  onSelect?: (item: ILine | undefined) => void;
  onKeyUp?: (() => void) | undefined;
  onFocus?: (() => void) | undefined;
}

// === SETTINGS ===
const maxNumberOfAutocompleteSuggestions = 20;

// === FUNCTIONS ===
function createResourceUrl(searchText: string): string {
  return `/api/line-autocomplete?q=${searchText}`;
}

function processReceivedData(data: unknown | undefined, q: string, filterResults?: (line: ILine) => boolean): IAutocompleteItem[] | undefined {
  if (!data || !q) {
    return undefined;
  }
  const businessModels = (data as VersionedLineApiModel[])?.map(VersionedLineApiModel.mapToBusinessModel) ?? [];
  return VersionedLineHelper.flattenAndFilterLinesData(businessModels, q, maxNumberOfAutocompleteSuggestions, filterResults);
}

function getSuggestionId(componentId: string, item: IAutocompleteItem): string | undefined {
  if (!item) return undefined;
  const line = item as ILine;
  return `autocomplete-result-item-${componentId}-${line.gid}-${line.appliesFrom?.toFormat('yyyy-MM-dd')}`;
}

function getItemId(item: IAutocompleteItem): string | undefined {
  return (item as ILine)?.gid;
}

function getDisplayName(item: IAutocompleteItem | undefined): string | undefined {
  const line = item as ILine;
  if (!line) {
    return undefined;
  }
  let name = LineHelper.getLineNameAndDirection(line);
  if (line.appliesFrom && line.appliesFrom > DateTime.now()) {
    name = `${name} (${LineHelper.getAppliesFromText(line, TranslationService.get('common.from'))})`;
  }
  return name;
}

function createSuggestionElement(suggestionId: string, item: IAutocompleteItem, isActive: boolean, suggestionSource: AutocompleteSuggestionSource, onClick: () => void): JSX.Element {
  const line = item as ILine;
  return (
    <li
      id={suggestionId}
      key={suggestionId}
      role="option"
      aria-selected={isActive}
      className={`autocomplete-result d-flex align-items-center py-2 px-2 m-0 w-100 border-x border-top ${isActive ? 'is-active' : ''}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
    >
      {(suggestionSource === AutocompleteSuggestionSource.history || suggestionSource === AutocompleteSuggestionSource.commonsearches) && (
        <span className="visually-hidden">
          {TranslationService.get(suggestionSource === AutocompleteSuggestionSource.history ? 'common.autocomplete.recentsearch' : 'common.autocomplete.commonsearch')}
        </span>
      )}
      <LineDescription line={line} showTransportMode={true} showDirectionName={true} showAppliesFromDateOnFutureLines={true} />
    </li>
  );
}

export default forwardRef<IAutocomplete & IValidatable, ILineAutocompleteProps>(function LineAutocomplete(
  {
    id,
    serverValidationName = undefined,
    labelText,
    labelClassName = undefined,
    labelPosition = AutocompleteLabelPosition.above,
    placeholderText,
    required = false,
    noResultsText,
    className = undefined,
    disabled = false,
    historyProps = undefined,
    filterResults = undefined,
    state,
    onSelect = () => {},
    onKeyUp,
    onFocus,
  }: ILineAutocompleteProps,
  ref
) {
  // === STATE ===
  const [searchText, setSearchText] = useState<string>('');

  // === REFS ===
  const autocompleteRef = useRef<IAutocomplete>(null);

  useImperativeHandle(ref, () => ({
    clear() {
      setSearchText('');
      autocompleteRef.current?.clear();
    },
    addSelectedItemToHistory(item: IAutocompleteItem) {
      autocompleteRef.current?.addSelectedItemToHistory(item);
    },
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${labelText} - `);
      ValidationEngine.for(updatedValidationInfo)
        .requiredIf(required, state.value !== undefined)
        .validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(serverValidationErrors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText} - `,
        serverValidationErrors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
        {}
      );
    },
    removeFromHistory(item: IAutocompleteItem) {
      autocompleteRef.current?.removeFromHistory?.(item);
    },
  }));

  return (
    <div>
      {labelPosition === AutocompleteLabelPosition.above && labelText && (
        <label id={`${id}-label`} htmlFor={id} className={labelClassName ?? ''}>
          {labelText}
          {required && <RequiredAsterisk />}
        </label>
      )}
      <Autocomplete
        selectedItem={state.value}
        searchText={searchText}
        id={id}
        ref={autocompleteRef}
        label={labelPosition === AutocompleteLabelPosition.inside ? labelText : undefined}
        placeholderText={placeholderText}
        required={required}
        noResultsText={noResultsText}
        className={className}
        disabled={disabled}
        historyProps={
          historyProps
            ? {
                ...historyProps,
                mapHistoryStringToItems: (s) =>
                  JSON.parse(s).map((x: { appliesFrom: string; appliesUpto: string }) => ({
                    ...x,
                    appliesFrom: DateTime.fromISO(x.appliesFrom),
                    appliesUpto: DateTime.fromISO(x.appliesUpto),
                  })) as ILine[],
                mapItemsToHistoryString: (x) => JSON.stringify(x),
              }
            : undefined
        }
        isInvalid={state.validationInfo.isInvalid}
        ariaDescribedBy={state.validationInfo.isInvalid ? `${id}-error` : undefined}
        listAriaLabelledBy={labelPosition === AutocompleteLabelPosition.above ? `${id}-label` : undefined}
        createResourceUrl={createResourceUrl}
        processReceivedData={(data, q) => processReceivedData(data, q, filterResults)}
        getSuggestionId={getSuggestionId}
        getItemId={getItemId}
        getDisplayName={getDisplayName}
        createSuggestionElement={createSuggestionElement}
        onSelect={(item: IAutocompleteItem | undefined) => {
          if (item !== undefined) {
            setSearchText('');
          }
          onSelect?.(item as ILine | undefined);
          onKeyUp?.();
        }}
        onSearchTextChange={(newSearchText: string) => {
          setSearchText(newSearchText);
          onKeyUp?.();
        }}
        onFocus={onFocus}
      />
      <div id={`${id}-error`} className={`invalid-feedback ${state.validationInfo.isInvalid ? 'd-block' : ''}`} aria-live="polite">
        {state.validationInfo.errors.map((error) => (
          <div key={error}>{error}</div>
        ))}
      </div>
    </div>
  );
});
